import React, { useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    getNotifications,
    getUnreadNotificationCount,
    markAllNotificationsAsRead,
} from '../../../../redux/Notification/Notificationslice';

const NotificationToggle = ({ className, notificationCount }) => {
    // Ensure the count is treated as a number
    const count = Number(notificationCount) || 0;

    return (
        <div className={`relative text-2xl notification ${className}`}>
            <svg width="17" height="19" viewBox="0 0 17 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.91166 16.6663C6.49927 17.185 7.27114 17.4997 8.11651 17.4997C8.96188 17.4997 9.73375 17.185 10.3214 16.6663M13.1165 5.83301C13.1165 4.50693 12.5897 3.23516 11.652 2.29747C10.7144 1.35979 9.44259 0.833008 8.11651 0.833008C6.79043 0.833008 5.51866 1.35979 4.58098 2.29747C3.6433 3.23516 3.11651 4.50693 3.11651 5.83301C3.11651 8.40816 2.4669 10.1713 1.74123 11.3375C1.12912 12.3212 0.82306 12.8131 0.834282 12.9503C0.846708 13.1022 0.878895 13.1602 1.00132 13.251C1.11189 13.333 1.61034 13.333 2.60722 13.333H13.6258C14.6227 13.333 15.1211 13.333 15.2317 13.251C15.3541 13.1602 15.3863 13.1022 15.3987 12.9503C15.41 12.8131 15.1039 12.3212 14.4918 11.3375C13.7661 10.1713 13.1165 8.40816 13.1165 5.83301Z" stroke="#FF8E1C" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>

            {/* Render red dot badge ONLY if count is greater than 0 */}
            {count > 0 && (
                <span style={{ fontSize: "12px" }} className="absolute -top-1 -right-2 bg-red-600 text-white font-bold rounded-full h-5 w-5 flex items-center justify-center">
                    {count}
                </span>
            )}
        </div>
    );
};

const Notification = ({ className }) => {
    const scrollContainerRef = useRef(null);
    const notificationRef = useRef(null);
    const [open, setOpen] = React.useState(false);
    const dispatch = useDispatch();

    const notificationsResponse = useSelector(state => state.notification.getNotifications.data);
    let notifications = [];
    if (Array.isArray(notificationsResponse?.data)) {
        notifications = notificationsResponse.data;
    } else if (typeof notificationsResponse === 'object' && Array.isArray(notificationsResponse?.data?.data)) {
        notifications = notificationsResponse.data.data;
    } else if (Array.isArray(notificationsResponse)) {
        notifications = notificationsResponse;
    }

    const unreadCountResponse = useSelector(state => state.notification.getUnreadNotificationCount.data);
    const unreadCount = parseInt(
        typeof unreadCountResponse === 'number'
            ? unreadCountResponse
            : unreadCountResponse?.count || 0,
        10
    );
    const loading = useSelector(state => state.notification.getNotifications.loading);

    useEffect(() => {
        dispatch(getUnreadNotificationCount());
        const interval = setInterval(() => {
            dispatch(getUnreadNotificationCount());
        }, 60000);
        return () => clearInterval(interval);
    }, [dispatch]);

    useEffect(() => {
        if (open) {
            dispatch(getNotifications());
        }
    }, [open, dispatch]);

    const handleToggle = () => setOpen(!open);
    
    const handleMarkAllAsRead = async () => {
        try {
            const response = await dispatch(markAllNotificationsAsRead());
            if (!response?.error) {
                dispatch(getNotifications());
                dispatch(getUnreadNotificationCount());
            }
        } catch (error) {
            console.error("Failed to mark all as read:", error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setOpen(false);
            }
        };
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [open]);

    const timeAgo = (dateInput) => {
        const date = new Date(dateInput);
        const now = new Date();
        const diffInMins = Math.floor((now - date) / 60000);

        if (diffInMins < 1) return 'Just now';
        if (diffInMins < 60) return `${diffInMins} min${diffInMins > 1 ? 's' : ''} ago`;
        const diffInHours = Math.floor(diffInMins / 60);
        if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    };

    const groupedNotifications = useMemo(() => {
        const groups = {};
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        notifications.forEach(notif => {
            const date = new Date(notif.created_at);
            let groupName = date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

            if (date.toDateString() === today.toDateString()) {
                groupName = 'Today';
            } else if (date.toDateString() === yesterday.toDateString()) {
                groupName = 'Yesterday';
            }

            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push(notif);
        });

        return groups;
    }, [notifications]);

    return (
        <div ref={notificationRef} className="relative inline-block">
            <style>
                {`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background-color: #CBD5E1; 
                    border-radius: 10px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background-color: #94A3B8; 
                }
                `}
            </style>

            <div onClick={handleToggle} className="cursor-pointer">
                <NotificationToggle className={className} notificationCount={unreadCount} />
            </div>

            {open && (
                <div className="absolute right-0 mt-2 w-[480px] bg-white rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 z-50 overflow-hidden">
                    
                    <div className="border-b border-gray-100 py-4 px-6 flex items-center justify-between bg-white z-10">
                        <h6 className="text-[17px] font-medium text-gray-800 tracking-wide">Notification</h6>
                        <div className="flex items-center gap-4">
                            <button
                                onClick={handleMarkAllAsRead}
                                className="text-blue-500 font-medium hover:text-blue-700 text-[14px] transition-colors"
                                disabled={loading || notifications.length === 0}
                            >
                                Mark all as read
                            </button>
                        </div>
                    </div>

                    <div ref={scrollContainerRef} className="overflow-y-auto max-h-[420px] custom-scrollbar pb-4 pt-2 bg-white">
                        {loading ? (
                            <div className="text-center p-8 text-gray-400 text-sm">Loading...</div>
                        ) : notifications.length === 0 ? (
                            <div className="text-center p-8 text-gray-400 text-sm">No notifications available</div>
                        ) : (
                            Object.keys(groupedNotifications).map((groupName, index) => (
                                <div key={index} className="mb-4 last:mb-0 text-left">
                                    <div className="text-[#FF8E1C] text-[15px] font-medium px-6 py-2">
                                        {groupName}
                                    </div>

                                    <ul className="flex flex-col gap-1">
                                        {groupedNotifications[groupName].map((notif) => (
                                            <li key={notif.notification_id} className="flex items-start justify-between py-3 px-6 hover:bg-gray-50/50 transition-colors border-b border-gray-200 last:border-0 ">
                                                <div className="flex items-start gap-3 flex-1 pr-4">
                                                    
                                                    {/* Orange dot wrapper completely removed if read */}
                                                    {!notif.is_read && (
                                                        <div className="w-2 pt-[6px] flex-shrink-0 flex justify-center">
                                                            <div className="w-2 h-2 rounded-full bg-[#FF8E1C]"></div>
                                                        </div>
                                                    )}
                                                    
                                                    <div className="flex-1">
                                                        <span className="text-gray-800 text-[15px] leading-snug break-words">
                                                            {notif.message}
                                                        </span>
                                                    </div>
                                                </div>

                                                <span className="text-gray-400 text-[13px] whitespace-nowrap pt-[2px]">
                                                    {timeAgo(notif.created_at)}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Notification;